import styled from 'styled-components';

import { ChevronDownIcon, SearchIcon } from '../../assets';
import { ChatWriteIcon, VehicleChatPlaceholder } from '../../assets';
import MobileCreateButton, { mobileCreateButtonFABPartial } from '../../components/Button/MobileCreateButton';
import ZIndexLayers from '../../services/ZIndexLayers';
import zIndexLayers from '../../services/ZIndexLayers/ZIndexLayers';
import { ThemeProp } from '../../theme/Themes';
import Avatar, { AvatarVariant } from '../Avatar';
import IconButton from '../Button/IconButton';
import Dropdown from '../Dropdown';
import Input from '../Input';
import { headerHeight } from '../layout/Header/Header.styles';
import Text from '../Text';

interface ChatListElementsProps {
  mobileLayout?: boolean;
}

const borderRadius = 'border-radius: 0.5rem 0.5rem 0rem 0rem';
const shadow = 'box-shadow: 0px -4px 32px rgba(0, 0, 0, 0.08)';

interface ContainerProps {
  $isCollapsed?: boolean;
  $fullScreen?: boolean;
}
export const Container = styled.div<ChatListElementsProps & ContainerProps>`
  display: flex;
  ${shadow};
  ${borderRadius};
  ${(props) => !props.mobileLayout && 'width: 22rem;'}
  ${(props) => !props.$isCollapsed && 'height: 39.375rem'};
  background: ${(props: ThemeProp) => props.theme.colors.background};
  z-index: ${zIndexLayers.fixedElement};
  flex: 1;
  flex-direction: column;
  padding-bottom: ${(props) => (!props.$isCollapsed ? '1rem' : 0)};
  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    border-radius: 0rem;
    background: ${(props: ThemeProp) => props.theme.colors.primary.c900};
  }
  ${(props: ContainerProps) => props.$fullScreen && 'position: fixed; height: 100vh; width: 100vw;'}
`;

export const MarginContainer = styled.div`
  margin: 0 0.75rem;
`;

export const PaddingContainer = styled.div`
  padding: 0 0.75rem 0 1.5rem;
  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    background: ${(props: ThemeProp) => props.theme.colors.background};
  }
`;

export const ChatListHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  min-height: ${headerHeight};
  cursor: pointer;
  padding: 1.125rem 0.75rem 1.125rem 1.5rem;
  align-items: center;
  gap: 1rem;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.ml};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};

  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    padding: 1rem;
    background: ${(props: ThemeProp) => props.theme.colors.background};
    gap: 0;
    box-shadow: ${(props: ThemeProp) => props.theme.shadows.chatBubble};
  }
`;

export const ChatListTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
`;

export const ChatListTitle = styled.div`
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  font-size: ${(props: ThemeProp) => props.theme.fontSize.ml};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.s};
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};

  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    font-size: ${(props: ThemeProp) => props.theme.fontSize.mlg};
    line-height: ${(props: ThemeProp) => props.theme.lineHeight.m};
  }
`;

export const ChatListCounter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 1.25rem;
  width: 1.25rem;
  font-size: 0.563rem;
  line-height: 0.688rem;
  color: ${(props: ThemeProp) => props.theme.colors.primary.c900};
  background-color: ${(props: ThemeProp) => props.theme.colors.primary.c200};
  border-radius: 1.5rem;
`;

export const Chevron = styled(ChevronDownIcon)`
  ${(props) => (props.collapsed ? 'transform: rotate(180deg);' : '')};
  stroke: ${(props: ThemeProp) => props.theme.colors.white};
`;

export const HeaderIcons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1.313rem;

  button {
    :hover {
      background: ${(props: ThemeProp) => props.theme.colors.primary.c200};
      border-radius: 3.125rem;
    }
  }
`;

export const Divider = styled.div`
  background-color: ${(props: ThemeProp) => props.theme.colors.greyscale.c500};
  height: 1px;
`;

export const TabContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
`;

export const TabSortContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.625rem;
  padding-top: 1.5rem;
`;

export const StyledDropdown = styled(Dropdown)`
  .nextui-button-text {
    font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
    font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
    line-height: ${(props: ThemeProp) => props.theme.lineHeight.m};
  }
`;

interface TabProps {
  $active?: boolean;
}
export const Tab = styled.div<TabProps>`
  font-weight: ${(props) => (props.$active ? props.theme.fontWeight.semibold : props.theme.fontWeight.regular)};
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.m};
  border-radius: 0.25rem;
  cursor: pointer;
  padding: 0.375rem 0.625rem;
  ${(props) =>
    props.$active
      ? `background-color:  ${props.theme.colors.secondary['12-light-blue']}; color:  ${props.theme.colors.primary.c400};`
      : ''};
`;

export const TabLabel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.313rem;
`;

interface SearchContainerProps {
  $shouldShow?: boolean;
}
export const SearchContainer = styled(MarginContainer)<SearchContainerProps>`
  ${(props) => (props.$shouldShow ? 'display: flex' : 'display: none')};

  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    margin: 1rem 1rem 0 1rem;
  }
`;

export const SearchInput = styled(Input)`
  && {
    margin: 0;
    margin-bottom: 0 !important;
    height: 2rem;
    border: 1px solid ${(props: ThemeProp) => props.theme.colors.greyscale.c800};
    border-radius: 100px;
    overflow: hidden;
    font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
    .nextui-input {
      font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
      margin-bottom: 0 !important;
    }
    .nextui-input::placeholder {
      font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
    }
    .nextui-input-main-container {
      margin-bottom: 0 !important;
    }
    div {
      margin-bottom: 0 !important;
    }
  }
`;

export const StyledSearchIcon = styled(SearchIcon)`
  stroke: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
`;

export const MessageTabIndicator = styled.div`
  width: 0.313rem;
  height: 0.313rem;
  background: ${(props: ThemeProp) => props.theme.colors.primary.c500};
  border-radius: 50%;
`;

export const ChatWindow = styled.div`
  background: transparent;
  position: fixed;
  bottom: 0;
  right: 25rem;
  min-width: 33rem;
  min-height: 40rem;
  z-index: ${ZIndexLayers.fixedElement};
  ${shadow};
  ${borderRadius};

  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    min-width: unset;
    min-height: unset;
    height: 100%; // NOTE: Using VH causes issues on mobile devices in browsers, but NOT in the PWA.
    width: 100vw;
    right: 0;
    box-shadow: none;
  }
`;

export const StyledChatWriteIcon = styled(ChatWriteIcon)`
  stroke: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
`;

export const BackButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 1rem;
  padding-left: 1.5rem;
  cursor: pointer;

  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    width: 100%;
    padding: 1rem 1rem 0 1rem;
  }

  svg {
    rotate: 90deg;
    path {
      stroke: ${(props: ThemeProp) => props.theme.colors.white};
    }
  }
`;

export const BackButtonText = styled(Text)`
  text-decoration-line: underline;
  cursor: pointer;
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.m};
  color: ${(props: ThemeProp) => props.theme.colors.white};
`;

export const VehicleMessageItemContainer = styled.div`
  margin: 0 1.5rem;
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.greyscale.c300};
  border-radius: 0.5rem 0.5rem 0 0;
`;

export const ListHeaderTitle = styled.div`
  font-size: 0.688rem;
  line-height: 1rem;
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
`;

interface MessagesContainerProps {
  $isVehicleDetails?: boolean;
}
export const MessagesContainer = styled.div<MessagesContainerProps>`
  padding: 0 0.75rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  padding-bottom: 2.5rem;
  ${(props) => props.$isVehicleDetails && 'padding: 0.75rem 0 0 0'};
  ${(props) => props.$isVehicleDetails && `background-color: ${props.theme.colors.greyscale.c500}`};
  border-radius: 0 0 0.5rem 0.5rem;
  ${(props) => props.$isVehicleDetails && `border: 1px solid ${props.theme.colors.greyscale.c300};`};

  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    padding: 0 1rem;
  }
`;

interface AvatarContainerProps {
  $isHeaderAvatar?: boolean;
}

export const AvatarContainer = styled.div<AvatarContainerProps>`
  position: relative;
  ${(props) => props.$isHeaderAvatar && `width: 2rem; height: 2rem`};
`;

export const AvatarImage = styled(Avatar).attrs(() => {
  return {
    variant: AvatarVariant.LARGE,
  };
})``;

export const ActiveIndicator = styled.div`
  position: absolute;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  left: 1.438rem;
  top: 1.438rem;
  z-index: ${zIndexLayers.overlay};
  background-color: ${(props) =>
    props.active ? props.theme.colors.system.success : props.theme.colors.greyscale.c500};
  border: 1.5px solid ${(props: ThemeProp) => props.theme.colors.white};
  box-sizing: content-box;
`;

export const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 0.625rem;
  padding: ${(props) =>
    props.$isVehicleDetails ? '0.375rem 0.75rem 0.25rem 0.75rem' : '0.375rem 0.5rem 0.75rem 0.5rem'};
  &:hover {
    background-color: ${(props: ThemeProp) => props.theme.colors.greyscale.c500};
    border-radius: ${(props) => (props.$isVehicleDetails ? '0' : '8px')};
  }
  cursor: pointer;
`;

export const RowTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const RowTitle = styled.div`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.m};
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
`;

export const RowSubtitle = styled.div`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xs};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
  line-height: 1rem;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c100};
`;

export const MessagesList = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-height: ${(props) => (props.$isVehicleDetails ? '18rem' : '24.8rem')};
  gap: ${(props) => (props.$isVehicleDetails ? '0.5rem' : '0.75rem')};
  overflow-y: auto;

  ::-webkit-scrollbar {
    background: transparent;
    width: 3px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 100px;
  }

  ::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors.greyscale.c300};
    width: 2px;
    border-radius: 100px;
  }
`;

interface VehicleChatsContainerProps {
  $addPaddingTop?: boolean;
}

export const VehicleChatsContainer = styled.div<VehicleChatsContainerProps>`
  margin-bottom: 0.5rem;
  padding-top: ${(props: VehicleChatsContainerProps) => (props.$addPaddingTop ? '1rem' : '0')};
`;

export const VehicleChatsSectionTitle = styled.div`
  display: flex;
  justify-content: space-between;
  flex: 1;
  font-size: 0.68rem;
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  line-height: 16px;
  text-transform: uppercase;
  padding: 0.5rem 0;
  align-items: center;
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c100};
`;

export const IconButtonContainer = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  cursor: pointer;
  border-radius: 0.5rem;

  &:hover {
    background: ${({ theme }: ThemeProp) => theme.colors.secondary['12-light-blue']};
  }

  svg {
    stroke: ${(props: ThemeProp) => props.theme.colors.primary.c300};
  }
`;

export const StyledVehicleChatPlaceholder = styled(VehicleChatPlaceholder)`
  width: 2rem;
  height: 2rem;
`;

export const BackButtonVehicleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  svg {
    rotate: 90deg;
    path {
      stroke: ${(props: ThemeProp) => props.theme.colors.white};
    }
  }
`;

export const StyledIconButton = styled(IconButton)`
  background-color: transparent;
`;

export const ChevronIconContainer = styled(IconButtonContainer)`
  svg {
    stroke: ${(props: ThemeProp) => props.theme.colors.white};
  }
`;

interface CreateMsgFAB {
  $hide?: boolean;
}

export const CreateMsgFAB = styled(MobileCreateButton)<CreateMsgFAB>`
  ${mobileCreateButtonFABPartial};
  position: fixed;
  bottom: 5.25rem;
  right: 1.25rem;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.375rem;

  svg {
    width: 1.5rem;
    height: 1.5rem;
    path {
      stroke: ${(props: ThemeProp) => props.theme.colors.black};
    }
  }

  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    display: ${(props: CreateMsgFAB) => (props.$hide ? 'none' : 'flex')};
    bottom: 5.25rem;
  }
`;

export const VehicleHeaderText = styled.span`
  display: block;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  margin-left: 0.5rem;
`;
